<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-dialog v-model="isDialogVisible" persistent max-width="900px">
    <v-card>
      <v-card-title class="d-flex align-center py-2 px-4">
        <span class="subtitle-1 font-weight-medium">Dynamic Cron Job</span>
        <span class="caption grey--text ml-2">(CST Timezone)</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeDialog">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4 pb-0">
        <v-row>
          <!-- LEFT: identity + params + filename -->
          <v-col cols="12" md="7" class="pr-md-4">
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.description"
                  v-uppercase="globalSettingItem.conEverythingToCaps"
                  label="Cron Job Name"
                  dense
                  autofocus
                  @blur="somethingChanged"
                  @keydown.enter="somethingChanged"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <template v-if="presetDestination">
                  <div class="caption grey--text">Report</div>
                  <div class="body-2 mb-2">{{ presetDestinationLabel }}</div>
                </template>
                <v-autocomplete
                  v-else
                  v-model="localItem.destination"
                  label="Report URL"
                  :items="reportRoutes"
                  item-text="label"
                  item-value="url"
                  dense
                  clearable
                  @change="somethingChanged"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.outputFilename"
                  label="Output Filename"
                  :hint="`$TODAY = run date, $MONTH = month covered (CST). Default: ${defaultFilenameForDestination}`"
                  persistent-hint
                  dense
                  clearable
                  @blur="somethingChanged"
                >
                  <template v-slot:append>
                    <v-btn x-small text color="primary"
                      @click="localItem.outputFilename = defaultFilenameForDestination">
                      Reset
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="align-center">
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="auditEnabled"
                  label="Enable record-count audit"
                  dense
                  hide-details
                  @change="onAuditToggle"
                ></v-checkbox>
              </v-col>
              <v-col v-if="auditEnabled" cols="12" sm="6">
                <v-select
                  v-model="recordCountKey"
                  label="Count column"
                  :items="recordCountOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <div class="d-flex align-center mt-4 mb-1">
              <span class="caption grey--text">Parameters</span>
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on }">
                  <v-icon x-small class="ml-1 grey--text" v-on="on">{{ icons.mdiInformation }}</v-icon>
                </template>
                <span>Filters not listed here apply no restriction — all records are included for that field.</span>
              </v-tooltip>
            </div>
            <v-row
              v-for="(row, idx) in paramRows"
              :key="idx"
              dense
              class="align-center"
            >
              <v-col cols="5" sm="4">
                <v-text-field
                  v-model="row.key"
                  label="Key"
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model="row.value"
                  label="Value"
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="1" sm="2" class="d-flex align-center">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn x-small outlined color="primary" v-on="on">Preset</v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="preset in paramPresets"
                      :key="preset.label"
                      @click="setPreset(idx, preset.value)"
                    >
                      <v-list-item-title>{{ preset.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
              <v-col cols="1" class="d-flex align-center">
                <v-icon small @click="removeParam(idx)">{{ icons.mdiDelete }}</v-icon>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-btn x-small text color="primary" @click="addParam">
                  <v-icon small>{{ icons.mdiPlus }}</v-icon> Add Param
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <!-- RIGHT: schedule + active toggle -->
          <v-col cols="12" md="5">
            <v-sheet outlined rounded class="pa-3">
              <div class="caption grey--text mb-3">Schedule (CST)</div>
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="localItem.expectedRunTime.frequency"
                    label="Repeat"
                    :items="frequencies"
                    dense
                    @change="onFrequencyChange"
                  ></v-select>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-if="isMonthly"
                    v-model="localItem.expectedRunTime.monthDay"
                    label="Day of month"
                    :items="monthDays"
                    dense
                    @change="somethingChanged"
                  ></v-select>
                  <v-text-field
                    v-else
                    v-model="localItem.expectedRunTime.days"
                    label="Days (*, 0-7, or range e.g. 1-5)"
                    :rules="[cronDaysRule]"
                    dense
                    @focus="$event.target.select()"
                    @blur="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.hrs"
                    label="Hrs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                    @keydown.tab="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.mins"
                    label="Mins"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                    @blur="somethingChanged"
                    @keydown.enter="somethingChanged"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.secs"
                    label="Secs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div v-if="isMonthly" class="caption grey--text mt-1">
                Dates auto-set to the full month ($MONTHSTART → $MONTHEND) at each run.
              </div>
              <div v-if="isMonthly && unpairedDateKeys.length" class="caption warning--text mt-1">
                This report takes a single date ({{ unpairedDateKeys.join(', ') }}); the month range
                does not apply — set the date manually.
              </div>
              <v-divider class="my-2"></v-divider>
              <!-- Segmented: the switch showed only the state it was in, so a job that was
                   Inactive read as the word "Inactive" beside a knob with nothing to compare
                   it to. Writes the same flag and still marks the dialog dirty. -->
              <BinaryToggle
                class="mt-2"
                id="cronJobDialogActiveToggle"
                aria-label="Job state: inactive or active"
                off-label="Inactive"
                on-label="Active"
                :value="localItem.isActiveInactive"
                @input="val => { localItem.isActiveInactive = val; somethingChanged(); }" />
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-4 pb-3 pt-2">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeDialog">CLOSE</v-btn>
        <v-btn color="blue darken-1" @click="save">SAVE</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapActions, mapMutations, mapGetters, mapState,
} from 'vuex';
import {
  mdiDelete, mdiPlus, mdiClose, mdiInformation,
} from '@mdi/js';
import BinaryToggle from '../common/base/BinaryToggle.vue';
import { amountToNumber } from '../../util/shared/vue-global';
import { normalizeCronDays, isValidCronDays } from '../../util/shared/cronDays';
import {
  FREQUENCIES,
  MONTH_DAYS,
  DEFAULT_MONTH_DAY,
  applyMonthRange,
  clearMonthRange,
  normalizeMonthDay,
} from '../../util/shared/cronSchedule';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

const normalizeUrl = (url) => (url && !url.startsWith('/') ? `/${url}` : url || '');

const slugFromUrl = (url) => (url || '').split('/').filter(Boolean).pop() || 'report';

const emptyItem = () => ({
  description: '',
  destination: '',
  params: {},
  outputFilename: '',
  expectedRunTime: {
    frequency: 'weekly', days: '*', monthDay: DEFAULT_MONTH_DAY, hrs: 0, mins: 0, secs: 0,
  },
  isActiveInactive: false,
});

export default {
  name: 'DynamicCronJobDialog',
  components: { BinaryToggle },
  mixins: [GlobalToCaps],
  data() {
    return {
      localItem: emptyItem(),
      paramRows: [],
      frequencies: FREQUENCIES,
      monthDays: MONTH_DAYS,
      // Date keys the month range could not be applied to (a from-key with no matching to-key —
      // the Cashiers Report is single-date). Warned about rather than guessed at.
      unpairedDateKeys: [],
      auditEnabled: false,
      recordCountKey: null,
      recordCountOptions: [
        { label: 'Unpaids', value: 'UnpaidsRecCt' },
        { label: 'Paids', value: 'PaidsRecCt' },
        { label: 'Paid Details', value: 'PaidsDetRecCt' },
        { label: 'Adjustments', value: 'AdjustRecCt' },
      ],
      icons: {
        mdiDelete, mdiPlus, mdiClose, mdiInformation,
      },
      paramPresets: [
        { label: 'Current Date', value: '$TODAY' },
        { label: 'Month Start', value: '$MONTHSTART' },
        { label: 'Month End', value: '$MONTHEND' },
        { label: 'Prev Month Start', value: '$PREVMONTHSTART' },
        { label: 'Prev Month End', value: '$PREVMONTHEND' },
        { label: 'Current FY', value: '$CURRENTFY' },
        { label: 'All Year', value: 'allYear' },
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
        { label: 'All', value: 'all' },
      ],
    };
  },
  props: {
    closeDialogBox: {
      type: Boolean,
      default: false,
    },
    baseCrudKey: {
      type: String,
      default: '',
    },
    rowItem: {
      type: Object,
      default: () => ({}),
    },
    presetDestination: {
      type: String,
      default: '',
    },
    presetParams: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters('base/crud', ['item', 'items', 'index', 'isVisible']),
    ...mapState({
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
      reportRoutes: (state) => (state.cron && state.cron.cronJobUtil ? state.cron.cronJobUtil.reportRoutes : []),
    }),
    isDialogVisible: {
      get() { return this.closeDialogBox; },
      set(value) { this.$emit('settingDialogVal', value); },
    },
    presetDestinationLabel() {
      const match = (this.reportRoutes || []).find((r) => r.url === this.presetDestination || r.url === `/${this.presetDestination}`);
      return match ? match.label : this.presetDestination;
    },
    isMonthly() {
      // An absent frequency reads as weekly — every pre-existing job doc.
      return (this.localItem.expectedRunTime || {}).frequency === 'monthly';
    },
    defaultFilenameForDestination() {
      const dest = this.localItem.destination || this.presetDestination || '';
      const params = this.localItem.params || this.presetParams || {};
      const ext = params.format === 'csv' ? 'csv' : 'pdf';
      // A monthly job is named for the month it COVERS: named by $TODAY, a report produced on
      // the last working day of February reads as a 27-February report.
      const dateToken = this.isMonthly ? '$MONTH' : '$TODAY';
      return `${slugFromUrl(dest)}_${dateToken}.${ext}`;
    },
    // The other cadence's default name, so switching Repeat can swap a still-default filename
    // without overwriting one the user typed.
    defaultFilenameForOtherCadence() {
      const dest = this.localItem.destination || this.presetDestination || '';
      const params = this.localItem.params || this.presetParams || {};
      const ext = params.format === 'csv' ? 'csv' : 'pdf';
      return `${slugFromUrl(dest)}_${this.isMonthly ? '$TODAY' : '$MONTH'}.${ext}`;
    },
  },
  watch: {
    closeDialogBox(val) {
      if (val) {
        this.$nextTick(() => {
          this.localItem = JSON.parse(JSON.stringify(this.rowItem || emptyItem()));
          this.localItem.destination = normalizeUrl(this.localItem.destination);
          // Pre-existing docs carry no frequency/monthDay: fill them in so the selects have a
          // value to bind to, without changing what the job does (absent frequency = weekly).
          const rt = this.localItem.expectedRunTime || {};
          this.localItem.expectedRunTime = {
            ...rt,
            frequency: rt.frequency === 'monthly' ? 'monthly' : 'weekly',
            days: normalizeCronDays(rt.days),
            monthDay: normalizeMonthDay(rt.monthDay),
          };
          if (this.presetDestination) {
            this.localItem.destination = normalizeUrl(this.presetDestination);
            this.localItem.params = { ...this.presetParams };
            this.$emit('update:rowItem', this.localItem);
            this.setItem([this.baseCrudKey, this.localItem]);
          }
          const params = this.localItem.params || {};
          // recordCountKey is surfaced via the audit control, not the generic param rows
          this.recordCountKey = params.recordCountKey || null;
          this.auditEnabled = !!params.recordCountKey;
          this.paramRows = Object.entries(params)
            .filter((entry) => entry[0] !== 'recordCountKey')
            .map((entry) => ({ key: entry[0], value: String(entry[1]) }));
          if (!this.localItem.outputFilename) {
            this.localItem.outputFilename = this.defaultFilenameForDestination;
          }
          // Read-only on open: surface the single-date warning for an already-monthly job
          // without rewriting any value the user saved.
          this.unpairedDateKeys = this.isMonthly ? applyMonthRange(params).unpaired : [];
        });
      } else {
        this.localItem = emptyItem();
        this.paramRows = [];
        this.unpairedDateKeys = [];
        this.auditEnabled = false;
        this.recordCountKey = null;
      }
    },
  },
  methods: {
    ...mapActions('cron/cronJobUtil', ['upsertCronJob', 'getCronJobs']),
    ...mapMutations('base/crud', ['setCriteria', 'setItems', 'setItem', 'setIndex']),
    closeDialog() {
      this.$emit('settingDialogVal', false);
      this.$nextTick(() => { this.setIndex([this.baseCrudKey, -1]); });
    },
    onAuditToggle(val) { if (!val) { this.recordCountKey = null; } },
    addParam() { this.paramRows.push({ key: '', value: '' }); },
    removeParam(idx) { this.paramRows.splice(idx, 1); },
    setPreset(idx, value) { this.paramRows[idx].value = value; },
    somethingChanged() {
      this.$emit('update:rowItem', this.localItem);
      this.setItem([this.baseCrudKey, this.localItem]);
    },
    cronDaysRule(v) {
      return isValidCronDays(v)
        || 'Use *, a day 0-7, an ascending range like 1-5, or a list like 1,3,5';
    },
    /**
     * Rewrite the visible param rows through a pure transform, in place. Working on the rows
     * (not on a rebuilt object) keeps their order and keeps a half-typed row intact.
     */
    rewriteParamRows(transform) {
      const params = {};
      this.paramRows.forEach((row) => { if (row.key) { params[row.key] = row.value; } });
      const next = transform(params);
      this.paramRows = this.paramRows.map((row) => (
        row.key && Object.prototype.hasOwnProperty.call(next, row.key)
          ? { ...row, value: String(next[row.key]) }
          : row));
    },
    /**
     * Switching Repeat auto-fills the date params, which is the whole point of the feature: the
     * user picks the report and the cadence, nothing else. The rewritten values stay visible in
     * the param rows so a saved job always runs what its row shows.
     */
    onFrequencyChange(value) {
      if (value === 'monthly') {
        this.localItem.expectedRunTime.monthDay = normalizeMonthDay(this.localItem.expectedRunTime.monthDay);
        let unpaired = [];
        this.rewriteParamRows((params) => {
          const result = applyMonthRange(params);
          unpaired = result.unpaired;
          return result.params;
        });
        this.unpairedDateKeys = unpaired;
      } else {
        this.rewriteParamRows((params) => clearMonthRange(params));
        this.unpairedDateKeys = [];
      }
      // Swap the filename's date token only while it is still the other cadence's default —
      // never overwrite a name the user typed.
      if (this.localItem.outputFilename === this.defaultFilenameForOtherCadence) {
        this.localItem.outputFilename = this.defaultFilenameForDestination;
      }
      this.somethingChanged();
    },
    async save() {
      const item = JSON.parse(JSON.stringify(this.localItem));
      item.destination = normalizeUrl(item.destination);
      const { expectedRunTime } = item;
      if (expectedRunTime.frequency === 'monthly') {
        // monthDay is 'L'/'LW' or a day string — same rule as days: never amountToNumber it.
        expectedRunTime.monthDay = normalizeMonthDay(expectedRunTime.monthDay);
        delete expectedRunTime.days; // a monthly job must not persist a stale weekday token
      } else {
        expectedRunTime.frequency = 'weekly';
        // Days is a cron day-of-week token (e.g. '1-5' = Mon-Fri) — keep it as a string, do NOT run it
        // through amountToNumber (that collapses '1-5' to 1 and '1,3,5' to 135, so a range never saves).
        expectedRunTime.days = normalizeCronDays(expectedRunTime.days);
        if (!isValidCronDays(expectedRunTime.days)) return; // invalid; the field rule shows why
        delete expectedRunTime.monthDay;
      }
      expectedRunTime.hrs = amountToNumber(expectedRunTime.hrs);
      expectedRunTime.mins = amountToNumber(expectedRunTime.mins);
      expectedRunTime.secs = amountToNumber(expectedRunTime.secs);
      delete item.nextExpectedRunTime;
      item.params = {};
      this.paramRows.forEach((row) => { if (row.key) { item.params[row.key] = row.value; } });
      // Opt-in record-count audit → params.recordCountKey
      if (this.auditEnabled && this.recordCountKey) {
        item.params.recordCountKey = this.recordCountKey;
      } else {
        delete item.params.recordCountKey;
      }
      await this.upsertCronJob({ item, baseCrudKey: this.baseCrudKey }).then(() => {
        this.closeDialog();
      });
    },
  },
};
</script>
